import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus, Edit2, Trash2, Search, X, RefreshCw,
  Receipt, Calendar, User, DollarSign, TrendingUp,
  Save, RotateCcw, AlertCircle, CheckCircle,
  Calculator, ChevronRight, CheckSquare, Clock, Printer, HelpCircle
} from 'lucide-react';
import apiClient from '../services/api';

const VIOLET     = '#667eea';
const VIOLET_MID = '#7a5cbf';
const VIOLET_DK  = '#764ba2';

/* ── printReceipt: all inline styles kept — renders in window.open context ── */
const printReceipt = async (r) => {
  try {
    const res = await apiClient.get(`/CustomerReceipts.php?id=${r.ReceiptID}`);
    if (!res.data?.success) { alert('Could not load receipt for printing'); return; }
    const full   = res.data.data;
    const allocs = full.Allocations || [];
    const fmt     = (n) => parseFloat(n || 0).toFixed(2);
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
    const isAdvance = full.ReceiptType === 'Advance' || allocs.length === 0;
    const allocRows = allocs.map((a, i) => `<tr style="background:${i%2===0?'#fff':'#f8fafc'}"><td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;font-family:monospace;font-weight:600;color:#2563eb;font-size:12px">${a.InvoiceNo||'—'}</td><td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;font-size:12px">${fmt(a.InvoiceAmount)}</td><td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;text-align:right;font-family:monospace;font-weight:700;font-size:12px;color:#059669">${fmt(a.AllocatedAmount)}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>${isAdvance?'Advance':'Payment'} Receipt - ${full.ReceiptNo}</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;padding:24px;color:#1a1a1a;font-size:13px;}table{border-collapse:collapse;width:100%;}@page{margin:15mm;}@media print{body{padding:0;}}</style></head><body><div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:18px;border-bottom:3px solid #1e3a5f"><div><div style="font-size:26px;font-weight:800;color:#1e3a5f;letter-spacing:-0.5px">${isAdvance?'ADVANCE RECEIPT':'PAYMENT RECEIPT'}</div><div style="font-size:13px;color:#6b7280;margin-top:4px">${isAdvance?'Advance payment against future invoices':'Invoice payment receipt'}</div></div><div style="text-align:right"><div style="font-size:22px;font-weight:800;color:#1e3a5f">LTMS</div><div style="font-size:11px;color:#6b7280;margin-top:2px">Logistics &amp; Transport Management</div><div style="margin-top:8px;background:#1e3a5f;color:#fff;padding:6px 14px;border-radius:6px;font-family:monospace;font-size:14px;font-weight:700;letter-spacing:0.04em">${full.ReceiptNo}</div></div></div><div style="display:flex;gap:12px;margin-bottom:20px;flex-wrap:wrap">${[[`Date`,fmtDate(full.ReceiptDate)],[`Payment Mode`,full.PaymentMode||'—'],full.AccountName?[`Account`,full.AccountName]:null,[`Status`,full.Status||'—'],full.ReferenceNo?[`Reference / UTR`,full.ReferenceNo]:null,full.ChequeNo?[`Cheque No`,full.ChequeNo+(full.ChequeDate?' · '+fmtDate(full.ChequeDate):'')]:null].filter(Boolean).map(([label,value])=>`<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;flex:1;min-width:120px"><div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:0.07em;margin-bottom:3px">${label.toUpperCase()}</div><div style="font-size:12px;font-weight:700;color:#111">${value}</div></div>`).join('')}</div><div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px 18px;margin-bottom:22px"><div style="font-size:11px;font-weight:700;color:#6b7280;letter-spacing:0.08em;margin-bottom:6px">RECEIVED FROM</div><div style="font-size:15px;font-weight:700;color:#111">${full.CustomerName||'—'}</div>${full.CustomerCode?`<div style="font-size:12px;color:#6b7280;font-family:monospace">${full.CustomerCode}</div>`:''}</div><div style="display:flex;justify-content:flex-end;margin-bottom:${allocs.length>0?'22px':'28px'}"><div style="min-width:300px">${isAdvance?`<div style="display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #e2e8f0"><span style="color:#6b7280">Receipt Type</span><span style="font-weight:700;color:#92400e">Advance Payment</span></div>`:''}<div style="display:flex;justify-content:space-between;padding:10px 0 6px;border-top:2px solid #1e3a5f;margin-top:4px"><span style="font-weight:800;font-size:15px;color:#1e3a5f">AMOUNT RECEIVED</span><span style="font-family:monospace;font-weight:800;font-size:15px;color:#1e3a5f">${fmt(full.Amount)}</span></div></div></div>${allocs.length>0?`<table style="margin-bottom:22px"><thead><tr style="background:#1e3a5f"><th style="padding:10px 12px;color:#fff;font-size:11px;text-align:left;letter-spacing:0.05em">INVOICE NO</th><th style="padding:10px 12px;color:#fff;font-size:11px;text-align:right;letter-spacing:0.05em">INVOICE TOTAL</th><th style="padding:10px 12px;color:#fff;font-size:11px;text-align:right;letter-spacing:0.05em">AMOUNT APPLIED</th></tr></thead><tbody>${allocRows}</tbody><tfoot><tr style="background:#f0fdf4"><td style="padding:10px 12px;font-weight:700;border-top:2px solid #1e3a5f">Total Applied</td><td style="border-top:2px solid #1e3a5f"></td><td style="padding:10px 12px;text-align:right;font-family:monospace;font-weight:800;color:#059669;font-size:14px;border-top:2px solid #1e3a5f">${fmt(full.Amount)}</td></tr></tfoot></table>`:`<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:12px 16px;margin-bottom:22px;font-size:12px;color:#92400e"><strong>Advance Payment</strong> — This amount will be applied against future invoices.</div>`}${full.Notes?`<div style="border-top:1px solid #e2e8f0;padding-top:14px;margin-bottom:20px"><div style="font-size:11px;font-weight:700;color:#6b7280;letter-spacing:0.08em;margin-bottom:6px">NOTES</div><div style="font-size:12px;color:#374151;line-height:1.6">${full.Notes}</div></div>`:''}<div style="margin-top:40px;border-top:2px solid #e2e8f0;padding-top:16px;display:flex;justify-content:space-between;align-items:flex-end"><div style="font-size:11px;color:#9ca3af">Generated by LTMS · ${new Date().toLocaleString('en-IN')}</div><div style="text-align:right"><div style="font-size:11px;color:#6b7280;margin-bottom:36px">Authorised Signatory</div><div style="border-top:1px solid #374151;padding-top:4px;font-size:11px;color:#6b7280;min-width:160px">Signature</div></div></div><script>window.onload=function(){window.print();}<\/script></body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { alert('Allow popups to print'); return; }
    w.document.write(html); w.document.close();
  } catch (err) { alert('Print failed: ' + err.message); }
};

/* ══════════════════════════════════════════════════════════════
   CustomerReceiptForm
════════════════════════════════════════════════════════════════ */
const CustomerReceiptForm = ({ editData = null, onClose, onSaved }) => {
  const [loading,     setLoading]     = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [showHelp,    setShowHelp]    = useState(false);

  const [customers,     setCustomers]     = useState([]);
  const PAYMENT_MODES = [{ id:'Cash', label:'Cash' }, { id:'Bank', label:'Bank' }];
  const [accounts,     setAccounts]     = useState([]);
  const [loadingAccts, setLoadingAccts] = useState(false);
  const [invoices,     setInvoices]     = useState([]);
  const [loadingInv,   setLoadingInv]   = useState(false);

  const skipAccountReset = useRef(false);
  const pendingAccountID = useRef('');

  const [form, setForm] = useState({
    ReceiptNo:'', ReceiptDate: new Date().toISOString().slice(0,10),
    CustomerID:'', PaymentMode:'', AccountID:'', Amount:'',
    ReferenceNo:'', ChequeNo:'', ChequeDate:'', Notes:'', Status:'Received',
  });

  const [allocations, setAllocations] = useState([]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => { loadInitialData(); }, [editData]); // eslint-disable-line

  const loadInitialData = async () => {
    setLoadingData(true); setError('');
    try {
      const [custRes, numRes] = await Promise.all([
        apiClient.get('/CustomerReceipts.php?outstanding-customers=1'),
        editData ? Promise.resolve(null) : apiClient.get('/CustomerReceipts.php/next-number'),
      ]);
      if (custRes.data?.success) setCustomers(custRes.data.data || []);
      if (editData) { await loadForEdit(editData); }
      else if (numRes?.data?.success) { setForm(p => ({ ...p, ReceiptNo: numRes.data.data.receiptNo })); }
    } catch (err) { setError('Failed to load: ' + (err.response?.data?.message || err.message)); }
    finally { setLoadingData(false); }
  };

  const loadForEdit = async (r) => {
    let full = r;
    if (!r.Allocations) {
      try { const res = await apiClient.get(`/CustomerReceipts.php?id=${r.ReceiptID}`); if (res.data?.success) full = res.data.data; } catch {}
    }
    if (full.Allocations?.length) {
      setAllocations(full.Allocations.map(a => {
        const dbBalance = parseFloat(a.BalanceAmount||0), allocatedAmt = parseFloat(a.AllocatedAmount||0);
        return { InvoiceID:String(a.InvoiceID), InvoiceNo:a.InvoiceNo, InvoiceDate:a.InvoiceDate||'', InvoiceAmount:parseFloat(a.InvoiceAmount||0), BalanceAmount:dbBalance+allocatedAmt, Status:a.Status||'Partially Paid', allocated:allocatedAmt.toFixed(2), _originalAlloc:allocatedAmt };
      }));
    }
    const paymentMode = full.PaymentMode || full.PaymentModeID || '';
    skipAccountReset.current = true;
    pendingAccountID.current = String(full.BankAccountID || full.AccountID || '');
    setForm({ ReceiptNo:full.ReceiptNo||'', ReceiptDate:(full.ReceiptDate||'').slice(0,10), CustomerID:String(full.CustomerID||''), PaymentMode:paymentMode, AccountID:'', Amount:full.Amount||'', ReferenceNo:full.ReferenceNo||'', ChequeNo:full.ChequeNo||'', ChequeDate:(full.ChequeDate||'').slice(0,10), Notes:full.Notes||'', Status:full.Status||'Received' });
  };

  useEffect(() => {
    if (!form.PaymentMode) { setAccounts([]); if (!skipAccountReset.current) setForm(p => ({ ...p, AccountID:'' })); return; }
    setLoadingAccts(true);
    if (!skipAccountReset.current) setForm(p => ({ ...p, AccountID:'' }));
    apiClient.get(`/CustomerReceipts.php?accounts-by-group=${form.PaymentMode}`)
      .then(res => {
        const accts = res.data?.success ? res.data.data || [] : [];
        setAccounts(accts);
        skipAccountReset.current = false;
        if (pendingAccountID.current) { const pending = pendingAccountID.current; pendingAccountID.current = ''; setForm(p => ({ ...p, AccountID: pending })); }
      })
      .catch(() => { setAccounts([]); skipAccountReset.current = false; pendingAccountID.current = ''; })
      .finally(() => setLoadingAccts(false));
  }, [form.PaymentMode]); // eslint-disable-line

  useEffect(() => {
    if (!form.CustomerID) { setInvoices([]); setAllocations([]); return; }
    setLoadingInv(true);
    apiClient.get(`/CustomerReceipts.php?unpaid-invoices=${form.CustomerID}`)
      .then(res => {
        if (res.data?.success) {
          const pending = res.data.data || [];
          setInvoices(pending);
          if (editData) {
            setAllocations(prev => {
              const allocatedIDs = new Set(prev.map(a => a.InvoiceID));
              const newRows = pending.filter(inv => !allocatedIDs.has(String(inv.InvoiceID))).map(inv => ({ InvoiceID:String(inv.InvoiceID), InvoiceNo:inv.InvoiceNo, InvoiceDate:inv.InvoiceDate||'', InvoiceAmount:parseFloat(inv.TotalAmount||0), BalanceAmount:parseFloat(inv.BalanceAmount||0), Status:inv.Status||'', allocated:'' }));
              const allocated = prev.filter(a => parseFloat(a.allocated||0)>0||a._originalAlloc>0);
              const unalloc   = prev.filter(a => !parseFloat(a.allocated||0)&&!a._originalAlloc);
              return [...allocated,...unalloc,...newRows];
            });
          } else {
            setAllocations(pending.map(inv => ({ InvoiceID:String(inv.InvoiceID), InvoiceNo:inv.InvoiceNo, InvoiceDate:inv.InvoiceDate||'', InvoiceAmount:parseFloat(inv.TotalAmount||0), BalanceAmount:parseFloat(inv.BalanceAmount||0), Status:inv.Status||'', allocated:'' })));
          }
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoadingInv(false));
  }, [form.CustomerID]); // eslint-disable-line

  const totalAllocated = useMemo(() => allocations.reduce((s,a) => s+parseFloat(a.allocated||0), 0), [allocations]);
  useEffect(() => { if (totalAllocated > 0) setForm(p => ({ ...p, Amount: totalAllocated.toFixed(2) })); }, [totalAllocated]);

  const activeAllocs = allocations.filter(a => parseFloat(a.allocated||0) > 0);
  const selectedCustomer = useMemo(() => customers.find(c => String(c.customerid) === String(form.CustomerID)) || null, [customers, form.CustomerID]);

  const handleFormChange = (e) => { const{name,value}=e.target; setForm(p=>({...p,[name]:value})); };
  const handleCustomerChange = (e) => { setForm(p=>({...p,CustomerID:e.target.value})); setAllocations([]); };
  const handleAllocChange = (id, val) => setAllocations(p => p.map(a => a.InvoiceID===id ? {...a,allocated:val} : a));
  const handlePayFull = (id, bal) => setAllocations(p => p.map(a => a.InvoiceID===id ? {...a,allocated:bal.toFixed(2)} : a));
  const handlePayAll  = () => setAllocations(p => p.map(a => ({...a,allocated:a.BalanceAmount.toFixed(2)})));
  const handleClearAlloc = () => { setAllocations(p => p.map(a=>({...a,allocated:''}))); setForm(p=>({...p,Amount:''})); };

  const handleSave = async () => {
    if (!form.CustomerID)  { setError('Please select a customer'); return; }
    if (!form.PaymentMode) { setError('Please select a payment mode (Cash or Bank)'); return; }
    if (!form.AccountID)   { setError(`Please select a ${form.PaymentMode==='Cash'?'cash':'bank'} account`); return; }
    const isAdvance = activeAllocs.length === 0;
    if (isAdvance && (!form.Amount || parseFloat(form.Amount) <= 0)) { setError('Please enter the advance amount'); return; }
    for (const a of activeAllocs) {
      if (parseFloat(a.allocated) > a.BalanceAmount + 0.005) { setError(`${a.InvoiceNo}: allocated ${parseFloat(a.allocated).toFixed(2)} exceeds balance ${a.BalanceAmount.toFixed(2)}`); return; }
    }
    setLoading(true); setError(''); setSuccess('');
    try {
      const payload = { ...form, Amount:isAdvance?parseFloat(form.Amount):totalAllocated, ReceiptType:isAdvance?'Advance':'Invoice', Allocations:activeAllocs.map(a=>({ InvoiceID:a.InvoiceID, InvoiceNo:a.InvoiceNo, InvoiceAmount:a.InvoiceAmount, BalanceAmount:a.BalanceAmount, AllocatedAmount:parseFloat(a.allocated) })), BankAccountID:form.AccountID||null, ...(editData?{ReceiptID:editData.ReceiptID}:{}) };
      const res = editData ? await apiClient.put('/CustomerReceipts.php', payload) : await apiClient.post('/CustomerReceipts.php', payload);
      if (res.data?.success) { setSuccess(`Receipt ${res.data.data?.ReceiptNo||form.ReceiptNo} ${editData?'updated':'saved'}!`); setTimeout(()=>onSaved(),900); }
      else throw new Error(res.data?.message);
    } catch (err) { setError(err.response?.data?.message||err.message); }
    finally { setLoading(false); }
  };

  const rowCount = activeAllocs.length;
  const inpCls  = 'w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-800 bg-white font-[inherit] outline-none transition-all duration-150 focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] box-border';
  const lblCls  = 'block text-[12.5px] font-semibold text-gray-500 uppercase tracking-wide mb-1';
  const spinStyle = { width:14, height:14, border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'cr-spin 0.7s linear infinite' };

  const TbBtn = ({ onClick, disabled, children, save, style: extraStyle }) => (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 border disabled:opacity-50 disabled:cursor-not-allowed ${save ? 'text-white border-violet-600' : 'bg-white text-gray-800 border-gray-200 hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50'}`}
      style={save ? { background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})`, ...extraStyle } : extraStyle}>
      {children}
    </button>
  );

  const SectionTitle = ({ icon: Icon, children, noMargin }) => (
    <div className={`flex items-center gap-2 text-[15px] font-semibold text-gray-800 pb-3 border-b-2 border-[#e9e4f8] ${noMargin ? 'm-0 border-0 pb-0' : 'mb-4'}`}>
      {Icon && <Icon size={15} style={{ color: VIOLET }} />}{children}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[#f5f5f8] rounded-xl w-full max-w-[1320px] max-h-[95vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 flex-shrink-0 border-b-[3px] border-white/15"
          style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
          <div className="flex items-center gap-5 flex-1">
            <div className="w-14 h-14 rounded-[14px] bg-white/25 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Receipt size={26} color="#fff" />
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-[1.6rem] font-bold text-white" style={{ textShadow:'0 2px 4px rgba(0,0,0,0.25)' }}>
                {editData ? 'Edit Customer Receipt' : 'New Customer Receipt'}
              </span>
              <span className="text-white text-[0.95rem] font-semibold bg-white/20 px-3.5 py-1 rounded-md border border-white/35 font-mono">🧾 {form.ReceiptNo||'—'}</span>
              {selectedCustomer && (
                <span className="text-white text-[0.95rem] font-semibold px-3.5 py-1 rounded-md border border-white/30" style={{ background:'rgba(56,161,105,0.85)' }}>{selectedCustomer.customername}</span>
              )}
              {totalAllocated > 0 ? (
                <span className="text-white text-[0.82rem] font-semibold bg-emerald-600/80 px-3 py-1 rounded-md border border-white/30">
                  {rowCount} invoice{rowCount!==1?'s':''} · {totalAllocated.toFixed(2)}
                </span>
              ) : parseFloat(form.Amount||0) > 0 ? (
                <span className="text-white text-[0.82rem] font-semibold px-3 py-1 rounded-md border border-white/30" style={{ background:'rgba(217,119,6,0.8)' }}>
                  Advance · {parseFloat(form.Amount).toFixed(2)}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setShowHelp(true)} title="How to fill this form"
              className="w-11 h-11 flex items-center justify-center rounded-xl border cursor-pointer transition-all duration-150 bg-white/18 border-white/30 hover:bg-white/30">
              <HelpCircle size={20} color="#fff" />
            </button>
            <button onClick={onClose} title="Close (ESC)"
              className="w-11 h-11 flex items-center justify-center rounded-xl border cursor-pointer transition-all duration-150 bg-white/18 border-white/30 hover:bg-white/30">
              <X size={20} color="#fff" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex gap-2.5 flex-wrap px-6 py-3.5 bg-white border-b border-gray-200 flex-shrink-0">
          <TbBtn save onClick={handleSave} disabled={loading||loadingData||(activeAllocs.length===0&&parseFloat(form.Amount||0)<=0)}>
            {loading ? <><span style={spinStyle} /> Saving…</> : <><Save size={16} /> {editData?'Update Receipt':'Save Receipt'}</>}
          </TbBtn>
          <TbBtn onClick={handleClearAlloc} disabled={loading}><RotateCcw size={16} /> Clear Allocation</TbBtn>
          {editData && (
            <TbBtn onClick={() => printReceipt(editData)} disabled={loading||loadingData}
              extraStyle={{ color:'#2563eb', borderColor:'#bfdbfe', background:'#eff6ff' }}>
              <Printer size={16} /> Print Receipt
            </TbBtn>
          )}
          {(totalAllocated>0||parseFloat(form.Amount||0)>0) && (
            <span className="text-sm text-gray-500 ml-2 self-center">
              {totalAllocated>0 ? `${rowCount} invoice${rowCount!==1?'s':''} → ${totalAllocated.toFixed(2)}` : `Advance → ${parseFloat(form.Amount).toFixed(2)}`}
            </span>
          )}
        </div>

        {error   && <div className="flex items-center gap-2 px-6 py-3 bg-red-50 border-b border-red-200 text-red-700 text-[13.5px] flex-shrink-0"><AlertCircle size={14} className="flex-shrink-0" /> {error}</div>}
        {success && <div className="px-6 py-3 bg-green-50 border-b border-green-300 text-green-800 text-[13.5px] flex-shrink-0">✓ {success}</div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-[#f5f5f8] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-violet-300 [&::-webkit-scrollbar-thumb]:rounded">
          {loadingData ? (
            <div className="flex flex-col items-center gap-3 py-20 text-gray-500">
              <span style={{ width:36,height:36,border:'3px solid #e9e4f8',borderTopColor:VIOLET,borderRadius:'50%',animation:'cr-spin 0.8s linear infinite',display:'inline-block' }} />
              <span>Loading…</span>
            </div>
          ) : (<>

            <div className="grid grid-cols-[1fr_320px] gap-4 mb-4 max-[1024px]:grid-cols-1">
              {/* Left: form */}
              <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                {/* Customer */}
                <div className="mb-4">
                  <label className={lblCls}>
                    <span className="flex items-center gap-1.5"><User size={13} style={{ color:VIOLET }} /> Customer
                      <span className="normal-case tracking-normal text-[11.5px] font-normal text-gray-400 ml-1">{customers.length} customers</span>
                    </span>
                  </label>
                  {editData ? (
                    <div className="flex items-center gap-3 px-3.5 py-2.5 bg-emerald-50 border-2 border-emerald-300 rounded-lg text-emerald-800 text-[0.95rem]">
                      <User size={16} className="text-emerald-600 flex-shrink-0" />
                      <div><div className="font-bold">{selectedCustomer?.customername||'—'}</div><div className="text-[0.75rem] text-gray-500 mt-0.5">Customer locked when editing</div></div>
                    </div>
                  ) : (
                    <select className={`${inpCls} border-2 border-violet-400 shadow-[0_0_0_3px_rgba(102,126,234,0.08)] text-[14.5px] font-medium cursor-pointer focus:border-violet-600`}
                      value={form.CustomerID} onChange={handleCustomerChange}>
                      <option value="">— Select a customer —</option>
                      {customers.map(c => (
                        <option key={c.customerid} value={c.customerid}>
                          {c.customername}{c.invoiceCount>0?` · ${c.invoiceCount} inv · Due: ${parseFloat(c.totalOutstanding).toFixed(2)}`:" · No outstanding"}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <SectionTitle icon={Receipt}>Receipt Details</SectionTitle>
                <div className="grid grid-cols-2 gap-4 max-[640px]:grid-cols-1">
                  <div><label className={lblCls}>Receipt No</label><input type="text" value={form.ReceiptNo} readOnly className={`${inpCls} bg-blue-50 font-bold text-blue-900 cursor-not-allowed`} /></div>
                  <div><label className={lblCls}>Status</label>
                    <select name="Status" value={form.Status} onChange={handleFormChange} className={`${inpCls} cursor-pointer`}>
                      {['Received','Cleared','Bounced','Cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Receipt Date *</label><input type="date" name="ReceiptDate" value={form.ReceiptDate} onChange={handleFormChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Payment Mode *</label>
                    <select name="PaymentMode" value={form.PaymentMode}
                      onChange={(e) => { skipAccountReset.current=false; pendingAccountID.current=''; handleFormChange(e); if (e.target.value==='Cash') setForm(p=>({...p,ChequeNo:'',ChequeDate:''})); }}
                      className={`${inpCls} cursor-pointer`}>
                      <option value="">— Select —</option>
                      {PAYMENT_MODES.map(pm=><option key={pm.id} value={pm.id}>{pm.label}</option>)}
                    </select>
                  </div>
                  {form.PaymentMode && (
                    <div>
                      <label className={lblCls}>
                        {form.PaymentMode==='Cash'?'Cash Account':'Bank Account'}
                        {loadingAccts && <span className="text-[11px] text-gray-400 ml-1.5">loading…</span>}
                      </label>
                      {accounts.length > 0 ? (
                        <select name="AccountID" value={form.AccountID} onChange={handleFormChange} className={`${inpCls} cursor-pointer`}>
                          <option value="">— Select Account —</option>
                          {accounts.map(ac=><option key={ac.achid} value={ac.achid}>{ac.accode?`${ac.accode} – ${ac.acname}`:ac.acname}</option>)}
                        </select>
                      ) : (
                        <input type="text" readOnly className={`${inpCls} bg-gray-100 cursor-not-allowed text-gray-400 text-[12px]`}
                          value={loadingAccts?'Loading accounts…':'No accounts found for this payment mode'} />
                      )}
                    </div>
                  )}
                  <div><label className={lblCls}>Reference / UTR No</label><input type="text" name="ReferenceNo" value={form.ReferenceNo} onChange={handleFormChange} placeholder="Transaction reference" className={inpCls} /></div>
                  {form.PaymentMode === 'Bank' && (<>
                    <div><label className={lblCls}>Cheque No</label><input type="text" name="ChequeNo" value={form.ChequeNo} onChange={handleFormChange} className={inpCls} /></div>
                    <div><label className={lblCls}>Cheque Date</label><input type="date" name="ChequeDate" value={form.ChequeDate} onChange={handleFormChange} className={inpCls} /></div>
                  </>)}
                  <div className="col-span-2 max-[640px]:col-span-1"><label className={lblCls}>Notes</label><textarea name="Notes" value={form.Notes} onChange={handleFormChange} rows={2} placeholder="Additional notes…" className={`${inpCls} resize-y min-h-[64px]`} /></div>
                </div>
              </div>

              {/* Right: summary */}
              <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm flex flex-col max-[1024px]:min-w-0">
                <SectionTitle icon={TrendingUp}>Receipt Summary</SectionTitle>
                <div className={`rounded-lg px-3.5 py-2.5 mb-3.5 border ${selectedCustomer?'bg-violet-50 border-violet-200':'bg-gray-50 border-gray-200'}`}>
                  {selectedCustomer ? (<>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">Customer</div>
                    <div className="text-sm font-semibold" style={{ color:VIOLET_MID }}>{selectedCustomer.customername}</div>
                    {selectedCustomer.customercode && <div className="text-[0.72rem] font-mono text-gray-400 mt-0.5">{selectedCustomer.customercode}</div>}
                    <div className="text-[0.75rem] text-gray-500 mt-1">
                      {selectedCustomer.invoiceCount>0 ? (<>
                        {selectedCustomer.invoiceCount} unpaid invoice{selectedCustomer.invoiceCount!==1?'s':''}
                        &nbsp;·&nbsp;
                        <span className="text-red-600 font-semibold">Due: {parseFloat(selectedCustomer.totalOutstanding).toFixed(2)}</span>
                      </>) : <span className="text-emerald-600 font-semibold">✓ No outstanding invoices</span>}
                    </div>
                  </>) : (
                    <div className="text-[0.83rem] text-gray-400 text-center py-2">Select a customer to begin</div>
                  )}
                </div>

                {activeAllocs.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-center py-6 text-[13px] leading-relaxed">
                    <CheckSquare size={40} className="mb-2.5 opacity-15" />
                    <p>Allocate payments below</p>
                  </div>
                ) : (
                  <div className="bg-violet-50 border border-violet-200 border-l-4 rounded-lg p-4 mt-3.5" style={{ borderLeftColor:VIOLET }}>
                    <div className="flex justify-between items-center py-1.5 border-b border-violet-100 text-sm"><span className="text-gray-500 font-medium">Invoices</span><span className="font-bold">{rowCount}</span></div>
                    <div className="flex justify-between items-center py-1.5 border-b border-violet-100 text-sm"><span className="text-gray-500 font-medium">Total Allocated</span><span className="font-mono font-semibold">{totalAllocated.toFixed(2)}</span></div>
                    <div className="flex justify-between items-center pt-2.5 border-t-2 mt-1 font-bold text-[16px]" style={{ borderTopColor:VIOLET }}>
                      <span className="text-gray-800 font-bold">Amount Received</span>
                      <span className="font-mono text-[14px] bg-emerald-100 px-2 py-0.5 rounded text-emerald-800">{totalAllocated>0?totalAllocated.toFixed(2):parseFloat(form.Amount||0).toFixed(2)}</span>
                    </div>
                    <div className="mt-3 flex flex-col gap-1">
                      {activeAllocs.map(a => (
                        <div key={a.InvoiceID} className="flex justify-between items-center bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 text-[12px]">
                          <span className="flex items-center gap-1"><CheckCircle size={11} color="#059669" /><span className="font-mono font-semibold text-blue-600">{a.InvoiceNo}</span></span>
                          <span className="font-mono font-bold text-emerald-700">{parseFloat(a.allocated).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Allocation table */}
            {form.CustomerID && (
              <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm mb-4">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3.5">
                  <SectionTitle icon={Calculator} noMargin>
                    Invoice-wise Allocation
                    {allocations.length>0 && <span className="inline-block bg-violet-100 text-violet-700 text-[12px] font-semibold px-2.5 py-0.5 rounded-full ml-2">{allocations.length} invoice{allocations.length!==1?'s':''}</span>}
                  </SectionTitle>
                  {allocations.length>0 && (
                    <div className="flex gap-2">
                      <button onClick={handlePayAll} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white border-0 cursor-pointer transition-all duration-150 bg-emerald-600 hover:bg-emerald-700">✓ Pay All Invoices</button>
                      <TbBtn onClick={handleClearAlloc}>Clear</TbBtn>
                    </div>
                  )}
                </div>
                <div>
                  {loadingInv ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-gray-500">
                      <span style={{ width:28,height:28,border:'3px solid #e9e4f8',borderTopColor:VIOLET,borderRadius:'50%',animation:'cr-spin 0.8s linear infinite',display:'inline-block' }} />
                      <span className="text-sm">Loading invoices…</span>
                    </div>
                  ) : allocations.length === 0 ? (
                    <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-xl p-6 mt-2">
                      <div className="font-bold text-[0.95rem] text-amber-800 mb-2">💰 Advance Receipt</div>
                      <p className="text-gray-500 text-[0.87rem] mb-4">No unpaid invoices found. This will be recorded as an advance payment.</p>
                      <div className="max-w-[240px]">
                        <label className={`${lblCls} text-amber-700`}>Advance Amount *</label>
                        <input type="number" step="0.01" min="0" name="Amount" value={form.Amount} onChange={handleFormChange} placeholder="0.00"
                          className={`${inpCls} font-mono font-bold text-[1.1rem] text-amber-800 border-amber-300 bg-amber-50`} />
                      </div>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[440px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-violet-300 [&::-webkit-scrollbar-thumb]:rounded">
                      <table className="w-full border-collapse text-[13.5px] bg-white">
                        <thead style={{ background:'linear-gradient(135deg,#f0ebff,#e8e0f8)' }}>
                          <tr>
                            {['Invoice No','Date','Status','Total','Balance Due','Allocate Amount','Pay Full'].map(h => (
                              <th key={h} className={`px-3.5 py-3 border-b-2 border-[#d6c9f5] text-[12.5px] font-semibold uppercase tracking-wide whitespace-nowrap ${['Total','Balance Due','Allocate Amount'].includes(h)?'text-right':'Pay Full'===h?'text-center':'text-left'}`}
                                style={{ color:VIOLET_MID }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allocations.map(a => {
                            const alloc  = parseFloat(a.allocated||0);
                            const hasVal = alloc > 0;
                            const over   = alloc > a.BalanceAmount + 0.005;
                            return (
                              <tr key={a.InvoiceID}
                                className={`border-b border-[#f0f0f8] ${over?'bg-red-50':hasVal?'bg-emerald-50':''}`}>
                                <td className="px-3.5 py-2.5">
                                  <span className="font-semibold font-mono text-[13px] text-blue-600">{a.InvoiceNo}</span>
                                </td>
                                <td className="px-3.5 py-2.5 text-[12px] text-gray-500">{a.InvoiceDate?new Date(a.InvoiceDate).toLocaleDateString('en-IN'):'—'}</td>
                                <td className="px-3.5 py-2.5">
                                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.Status?.toLowerCase()==='overdue'?'bg-red-50 text-red-600':'bg-blue-50 text-blue-600'}`}>{a.Status||'—'}</span>
                                </td>
                                <td className="px-3.5 py-2.5 text-right font-mono">{a.InvoiceAmount.toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-right font-mono font-bold text-red-600">{a.BalanceAmount.toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-right">
                                  <input type="number" step="0.01" min="0" max={a.BalanceAmount}
                                    value={a.allocated} placeholder="0.00"
                                    onChange={e => handleAllocChange(a.InvoiceID, e.target.value)}
                                    className="w-[110px] text-right px-2 py-1.5 rounded-md font-mono font-semibold outline-none transition-all duration-150"
                                    style={{ border:`1.5px solid ${over?'#ef4444':hasVal?'#10b981':'#d1d5db'}`, background:over?'#fef2f2':hasVal?'#f0fdf4':'#fff' }} />
                                  {over && <div className="text-[10px] text-red-500 mt-0.5">Max: {a.BalanceAmount.toFixed(2)}</div>}
                                </td>
                                <td className="px-3.5 py-2.5 text-center">
                                  <button type="button" onClick={() => handlePayFull(a.InvoiceID, a.BalanceAmount)}
                                    className="px-2.5 py-1 rounded-md text-[0.78rem] font-semibold cursor-pointer border transition-all duration-100"
                                    style={{ background:parseFloat(a.allocated)===a.BalanceAmount&&alloc>0?'#059669':'#fff', color:parseFloat(a.allocated)===a.BalanceAmount&&alloc>0?'#fff':'#059669', borderColor:'#059669' }}>
                                    {parseFloat(a.allocated)===a.BalanceAmount&&alloc>0?'✓ Full':'Pay Full'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-violet-50 border-t-2 border-violet-300">
                            <td colSpan={4} className="px-3.5 py-2.5 text-right font-semibold text-gray-500 text-[13px]">{allocations.length} invoice{allocations.length!==1?'s':''} — totals:</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-semibold text-red-600">{allocations.reduce((s,a)=>s+a.BalanceAmount,0).toFixed(2)}</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-bold text-emerald-700 text-[14px]">{totalAllocated.toFixed(2)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!form.CustomerID && (
              <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg text-center py-12 px-6 mb-4">
                <User size={44} className="mx-auto mb-3 opacity-10" />
                <h3 className="text-[1rem] text-gray-500 font-semibold m-0 mb-1.5">Select a customer above</h3>
                <p className="text-[0.87rem] text-gray-400 m-0">Their unpaid invoices will appear here for allocation</p>
              </div>
            )}
          </>)}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 px-6 py-3.5 bg-white border-t border-gray-200 flex-shrink-0">
          <span className="flex-1 text-[13px] text-gray-500 font-medium">
            {!form.CustomerID ? `${customers.length} customer${customers.length!==1?'s':''} available`
              : activeAllocs.length>0 ? `${activeAllocs.length} invoice${activeAllocs.length!==1?'s':''} · Total: ${totalAllocated.toFixed(2)}`
              : allocations.length>0 ? `${allocations.length} invoice${allocations.length!==1?'s':''} available`
              : 'Advance receipt — no invoices for this customer'}
          </span>
          <TbBtn onClick={onClose} disabled={loading}>Cancel</TbBtn>
          <TbBtn save onClick={handleSave} disabled={loading||loadingData||(activeAllocs.length===0&&parseFloat(form.Amount||0)<=0)}>
            {loading ? <><span style={spinStyle} /> Saving…</> : <><Save size={16} /> {editData?'Update Receipt':'Save Receipt'}</>}
          </TbBtn>
        </div>
      </div>

      {showHelp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1100] p-5"
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[620px] max-h-[85vh] flex flex-col shadow-2xl border border-gray-200 overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-7 py-5"
              style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
              <h3 className="text-lg font-bold m-0 flex items-center gap-2" style={{ color:'#fff' }}>
                <HelpCircle size={20} /> How to Record a Receipt
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white hover:bg-white/30 hover:rotate-90 transition-all duration-200">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-7 space-y-4">
              {[
                { title: 'Select a Customer', desc: 'Choose the customer this payment is from. Their unpaid invoices will load automatically below.' },
                { title: 'Receipt Details', desc: 'The Receipt No is auto-generated. Set the Receipt Date, Payment Mode (Cash or Bank), and the matching account — all required.' },
                { title: 'Allocate to Invoices', desc: 'For each unpaid invoice, type an amount to allocate, or click "Pay Full" to settle it completely. Use "Pay All Invoices" to auto-allocate across everything outstanding.' },
                { title: 'Advance Payment', desc: 'If the customer has no unpaid invoices, the receipt is recorded as an Advance — just enter the amount received.' },
                { title: 'Save & Print', desc: 'Click "Save Receipt" to record it. Once saved, use "Print Receipt" to generate a formatted receipt for the customer.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end px-7 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   CustomerReceipts — List page
════════════════════════════════════════════════════════════════ */
const statusBadgeCls = (s) => {
  const m = { received:'bg-blue-50 text-blue-700', cleared:'bg-green-50 text-green-700', bounced:'bg-red-50 text-red-600', cancelled:'bg-gray-100 text-gray-400' };
  return m[(s||'').toLowerCase()] || 'bg-gray-100 text-gray-600';
};

const CustomerReceipts = () => {
  const [receipts,     setReceipts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [showForm,     setShowForm]     = useState(false);
  const [editReceipt,  setEditReceipt]  = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [printingId,   setPrintingId]   = useState(null);
  const [showHelp,      setShowHelp]     = useState(false);

  const getSession = () => {
    try { const s = localStorage.getItem('ltms_session'); if (!s) return null; const d = JSON.parse(s); return d?.session||d; } catch { return null; }
  };
  const session = getSession();

  useEffect(() => { loadReceipts(); }, []);

  const loadReceipts = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/CustomerReceipts.php');
      if (res.data?.success) setReceipts(res.data.data || []);
      else throw new Error(res.data?.message || 'Failed to load');
    } catch (err) {
      if (err.response?.status===401) { setError('Session expired.'); setTimeout(()=>{ window.location.href='/login'; },2000); }
      else setError(err.response?.data?.message||err.message||'Failed to load receipts');
    } finally { setLoading(false); }
  };

  const handleNew   = ()  => { setEditReceipt(null); setShowForm(true); };
  const handleEdit  = (r) => { setEditReceipt(r);   setShowForm(true); };
  const handleClose = ()  => { setShowForm(false); setEditReceipt(null); };
  const handleSaved = ()  => { setShowForm(false); setEditReceipt(null); loadReceipts(); };
  const handlePrint = async (r) => { setPrintingId(r.ReceiptID); await printReceipt(r); setPrintingId(null); };

  const handleDelete = async (id, receiptNo) => {
    if (!window.confirm(`Delete receipt ${receiptNo}? This will reverse all invoice allocations.`)) return;
    try {
      const res = await apiClient.delete('/CustomerReceipts.php', { params: { id } });
      if (res.data?.success) loadReceipts(); else alert('Error: ' + res.data?.message);
    } catch (err) { alert('Error: ' + (err.response?.data?.message||err.message)); }
  };

  const statuses = ['All','Received','Cleared','Bounced','Cancelled'];
  const filtered = receipts.filter(r => {
    const matchSearch = (r.ReceiptNo||'').toLowerCase().includes(searchTerm.toLowerCase()) || (r.CustomerName||'').toLowerCase().includes(searchTerm.toLowerCase()) || (r.customername||'').toLowerCase().includes(searchTerm.toLowerCase()) || (r.ReferenceNo||'').toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch && (statusFilter==='All'||r.Status===statusFilter);
  });

  const totalAmount = filtered.reduce((s,r) => s+parseFloat(r.Amount||0), 0);
  const spinStyle = { width:14,height:14,border:'2px solid #9ca3af',borderTopColor:'transparent',borderRadius:'50%',display:'inline-block',animation:'cr-spin 0.7s linear infinite' };

  if (!session?.companyid) {
    return (
      <div className="min-h-screen bg-[#f7f8fc] flex items-center justify-center">
        <div className="text-center text-gray-500 py-20">
          <Receipt size={40} className="mx-auto mb-3 opacity-30" />
          <h3 className="font-bold text-gray-700 mb-1">No Session</h3>
          <p className="text-sm">Please select a company and financial year.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fc] font-[system-ui,sans-serif] text-gray-800">
      <style>{`@keyframes cr-spin{to{transform:rotate(360deg);}} .cr-tr:hover td{background:#faf8ff;}`}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-8 py-7 shadow-lg"
        style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-[14px] bg-white/25 flex items-center justify-center shadow-lg flex-shrink-0">
            <Receipt size={26} color="#fff" />
          </div>
          <div>
            <h1 className="m-0 text-[1.75rem] font-bold" style={{ color:'#fff', textShadow:'0 2px 4px rgba(0,0,0,0.2)' }}>Customer Receipts</h1>
            <p className="m-0 mt-1 text-sm" style={{ color:'rgba(255,255,255,0.8)' }}>Payments received &amp; invoice allocation</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={loadReceipts} disabled={loading} title="Refresh"
            className="w-10 h-10 flex items-center justify-center rounded-lg border cursor-pointer transition-all duration-150 bg-white/20 text-white border-white/30 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed">
            <RefreshCw size={16} style={loading?{animation:'cr-spin 1s linear infinite'}:{}} />
          </button>
          <button onClick={() => setShowHelp(true)} title="How this page works"
            className="w-10 h-10 flex items-center justify-center rounded-lg border cursor-pointer transition-all duration-150 bg-white/20 text-white border-white/30 hover:bg-white/30">
            <HelpCircle size={16} />
          </button>
          <button onClick={handleNew}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border-0 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 hover:bg-violet-50 hover:-translate-y-px shadow-md"
            style={{ color:VIOLET_MID }}>
            <Plus size={18} /> New Receipt
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4 px-8 pt-6 max-[1100px]:grid-cols-2">
        {[
          { icon:<Receipt size={20}/>,     cls:'bg-blue-50   text-blue-600',   value:filtered.length, label:'Total Receipts' },
          { icon:<TrendingUp size={20}/>,  cls:'bg-green-50  text-green-600',  value:totalAmount.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}), label:'Total Received' },
          { icon:<CheckCircle size={20}/>, cls:'bg-orange-50 text-orange-600', value:receipts.filter(r=>r.Status==='Cleared').length, label:'Cleared' },
          { icon:<AlertCircle size={20}/>, cls:'bg-red-50    text-red-500',    value:receipts.filter(r=>r.Status==='Bounced').length, label:'Bounced' },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-lg p-5 flex items-center gap-4 shadow-sm border border-gray-200">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${c.cls}`}>{c.icon}</div>
            <div><div className="text-[1.4rem] font-bold text-gray-800">{c.value}</div><div className="text-[0.8rem] text-gray-500 mt-0.5">{c.label}</div></div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-4 px-8 py-5 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input type="text" placeholder="Search receipt no, customer, reference..."
            value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.12)] box-border" />
          {searchTerm && <button onClick={()=>setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 bg-transparent border-0 text-gray-400 cursor-pointer hover:text-red-500 flex items-center p-0.5"><X size={14} /></button>}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {statuses.map(s => (
            <button key={s} onClick={()=>setStatusFilter(s)}
              className={`px-3.5 py-1.5 rounded-full border text-[13px] cursor-pointer whitespace-nowrap transition-all duration-150 ${statusFilter===s?'font-semibold text-white border-violet-500':'bg-white text-gray-500 border-gray-200 hover:border-violet-400 hover:text-violet-600'}`}
              style={statusFilter===s?{background:VIOLET,borderColor:VIOLET}:{}}>
              {s}
            </button>
          ))}
        </div>
        <div className="text-[13px] text-gray-500 whitespace-nowrap">{filtered.length} {filtered.length===1?'receipt':'receipts'}</div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center gap-3 py-16 text-gray-500">
          <span className="w-9 h-9 border-4 border-violet-100 border-t-violet-500 rounded-full block" style={{ animation:'cr-spin 0.8s linear infinite' }} />
          <span>Loading receipts...</span>
        </div>
      ) : error ? (
        <div className="mx-8 my-5 px-4 py-3.5 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-4">
          <strong>Error:</strong> {error}
          <button className="flex items-center gap-2 px-4 py-2 bg-white border-0 rounded-lg text-sm font-semibold cursor-pointer hover:bg-violet-50" style={{ color:VIOLET_MID }} onClick={loadReceipts}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-500 px-5">
          <Receipt size={52} className="mx-auto mb-3 opacity-20" />
          <h3 className="font-semibold text-gray-700 mb-1.5">{searchTerm||statusFilter!=='All'?'No matching receipts':'No receipts yet'}</h3>
          <p className="text-sm">{searchTerm||statusFilter!=='All'?'Try adjusting your search or filter':'Record your first customer payment to get started'}</p>
          {!searchTerm&&statusFilter==='All'&&(
            <button className="mt-4 flex items-center gap-2 px-5 py-2.5 mx-auto border-0 rounded-lg text-sm font-semibold text-white cursor-pointer"
              style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }} onClick={handleNew}>
              <Plus size={18} /> New Receipt
            </button>
          )}
        </div>
      ) : (
        <div className="px-8 pb-8 overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px] bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
            <thead style={{ background:'linear-gradient(135deg,#f0ebff,#e8e0f8)' }}>
              <tr>
                {['Receipt No','Receipt Date','Customer','Payment Mode / Account','Reference','Amount','Status','Actions'].map(h => (
                  <th key={h} className={`px-3.5 py-3 border-b-2 border-[#d6c9f5] text-[12.5px] font-semibold uppercase tracking-wide whitespace-nowrap ${'Amount'===h?'text-right':'Actions'===h?'text-center':'text-left'}`}
                    style={{ color:VIOLET_MID }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.ReceiptID} className="cr-tr border-b border-[#f0f0f8]">
                  <td className="px-3.5 py-3">
                    <span className="font-semibold cursor-pointer hover:underline" style={{ color:VIOLET }} onClick={() => handleEdit(r)} title="Click to edit">{r.ReceiptNo}</span>
                    {r.ReceiptType==='Advance' && <span className="text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-300 rounded px-1 py-0.5 ml-1.5">ADV</span>}
                  </td>
                  <td className="px-3.5 py-3">
                    <span className="flex items-center gap-1 text-gray-500 text-[13px]"><Calendar size={12} />{r.ReceiptDate?new Date(r.ReceiptDate).toLocaleDateString():'—'}</span>
                  </td>
                  <td className="px-3.5 py-3">
                    <span className="flex items-center gap-1.5 text-gray-500">
                      <User size={12} />
                      <span>
                        <div className="text-gray-800 font-medium text-[13px]">{r.CustomerName||r.customername||'—'}</div>
                        {r.CustomerCode && <div className="text-[11px] text-gray-400">{r.CustomerCode}</div>}
                      </span>
                    </span>
                  </td>
                  <td className="px-3.5 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full w-fit border"
                        style={{ background:r.PaymentMode==='Cash'?'#f0fdf4':'#eff6ff', color:r.PaymentMode==='Cash'?'#166534':'#1d4ed8', borderColor:r.PaymentMode==='Cash'?'#86efac':'#bfdbfe' }}>
                        {r.PaymentMode==='Cash'?'💵':'🏦'} {r.PaymentMode||'—'}
                      </span>
                      {r.AccountName && <span className="text-[11px] text-gray-500">{r.AccountName}</span>}
                    </div>
                  </td>
                  <td className="px-3.5 py-3 text-[12px] text-gray-500 font-mono">{r.ReferenceNo||r.ChequeNo||'—'}</td>
                  <td className="px-3.5 py-3 text-right font-mono font-bold">{parseFloat(r.Amount||0).toFixed(2)}</td>
                  <td className="px-3.5 py-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-xl text-[12px] font-semibold whitespace-nowrap ${statusBadgeCls(r.Status)}`}>{r.Status||'Received'}</span>
                  </td>
                  <td className="px-3.5 py-3">
                    <div className="flex gap-1 justify-center">
                      <button onClick={()=>handlePrint(r)} disabled={printingId===r.ReceiptID} title="Print Receipt"
                        className="w-8 h-8 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-150 disabled:cursor-not-allowed"
                        style={printingId===r.ReceiptID?{color:'#9ca3af',borderColor:'#e5e7eb',background:'#f9fafb'}:{color:'#2563eb',borderColor:'#bfdbfe',background:'#eff6ff'}}>
                        {printingId===r.ReceiptID ? <span style={spinStyle} /> : <Printer size={14} />}
                      </button>
                      <button onClick={()=>handleEdit(r)} title="Edit Receipt"
                        className="w-8 h-8 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-150 hover:bg-violet-50 hover:border-violet-300"
                        style={{ color:VIOLET, borderColor:'transparent', background:'none' }}>
                        <Edit2 size={14} />
                      </button>
                      <button onClick={()=>handleDelete(r.ReceiptID,r.ReceiptNo)} title="Delete Receipt"
                        className="w-8 h-8 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-150 hover:bg-red-50"
                        style={{ color:'#e53e3e', borderColor:'transparent', background:'none' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <CustomerReceiptForm editData={editReceipt} onClose={handleClose} onSaved={handleSaved} />}

      {showHelp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[600px] max-h-[85vh] flex flex-col shadow-2xl border border-gray-200 overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-7 py-5"
              style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
              <h3 className="text-lg font-bold m-0 flex items-center gap-2" style={{ color:'#fff' }}>
                <HelpCircle size={20} /> How to Use Customer Receipts
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white hover:bg-white/30 hover:rotate-90 transition-all duration-200">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-7 space-y-4">
              {[
                { title: 'Summary Cards', desc: 'At the top: total receipts, total amount received, cleared count, and bounced count — all reflecting the currently filtered list.' },
                { title: 'Search & Filter', desc: 'Search by receipt number, customer, or reference. Use the status pills to narrow the list further.' },
                { title: 'Record a Receipt', desc: 'Click "New Receipt" to record a customer payment and allocate it against their unpaid invoices, or as an advance if none are outstanding.' },
                { title: 'Advance Receipts', desc: 'Receipts marked "ADV" are advance payments not yet applied to a specific invoice.' },
                { title: 'Delete a Receipt', desc: 'Deleting a receipt reverses all its invoice allocations, so use it carefully.' },
                { title: 'Print', desc: 'Click the print icon on any row to generate a formatted receipt for the customer.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end px-7 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerReceipts;